/* eslint-disable max-lines */
import { emailRegEx, numberAndAlphabetRegEx, phoneRegEx, usernameRegEx } from '@logto/core-kit';
import { z } from 'zod';
import { AdditionalIdentifier, MfaFactor, SignInIdentifier, jsonObjectGuard, webAuthnTransportGuard, } from '../foundations/index.js';
import { InteractionEvent } from './interaction-event.js';
import { emailVerificationCodePayloadGuard, phoneVerificationCodePayloadGuard, } from './verification-code.js';
export { eventGuard, InteractionEvent } from './interaction-event.js';
export const verificationIdentifierGuard = z.object({
    type: z.union([z.nativeEnum(SignInIdentifier), z.nativeEnum(AdditionalIdentifier)]),
    value: z.string(),
});
export const interactionIdentifierGuard = z.object({
    type: z.nativeEnum(SignInIdentifier),
    value: z.string(),
});
export const verificationCodeIdentifierGuard = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(SignInIdentifier.Email),
        // `.max(256)` caps the input length as defense-in-depth for downstream email processing
        // (a valid address is at most 254 chars per RFC 5321).
        value: z.string().max(256).regex(emailRegEx),
    }),
    z.object({
        type: z.literal(SignInIdentifier.Phone),
        value: z.string().regex(phoneRegEx),
    }),
]);
export const socialAuthorizationUrlPayloadGuard = z.object({
    state: z.string(),
    redirectUri: z.string(),
    scope: z.string().optional(),
});
export const socialVerificationCallbackPayloadGuard = z.object({
    connectorData: jsonObjectGuard,
    verificationId: z.string().optional(),
});
export const passwordVerificationPayloadGuard = z.object({
    identifier: interactionIdentifierGuard,
    password: z.string().min(1),
});
export const totpVerificationVerifyPayloadGuard = z.object({
    code: z.string().min(1),
    verificationId: z.string().optional(),
});
export const backupCodeVerificationVerifyPayloadGuard = z.object({
    code: z.string().min(1),
});
export const oneTimeTokenVerificationVerifyPayloadGuard = z.object({
    identifier: z.object({
        type: z.literal(SignInIdentifier.Email),
        value: z.string().regex(emailRegEx),
    }),
    token: z.string().min(1),
});
export const identificationApiPayloadGuard = z.object({
    verificationId: z.string().optional(),
    linkSocialIdentity: z.boolean().optional(),
});
export const CreateExperienceApiPayloadGuard = z.object({
    interactionEvent: z.nativeEnum(InteractionEvent),
    captchaToken: z.string().optional(),
});
/** Payload type for `POST /api/experience/profile */
export const updateProfileApiPayloadGuard = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(SignInIdentifier.Username),
        value: z.string().regex(usernameRegEx),
    }),
    z.object({
        type: z.literal('password'),
        value: z.string(),
    }),
    z.object({
        type: z.literal(SignInIdentifier.Email),
        verificationId: z.string(),
    }),
    z.object({
        type: z.literal(SignInIdentifier.Phone),
        verificationId: z.string(),
    }),
    z.object({
        type: z.literal('social'),
        verificationId: z.string(),
    }),
    z.object({
        type: z.literal('extraProfile'),
        values: z.record(z.string().regex(numberAndAlphabetRegEx), z.unknown()),
    }),
]);
// ====== Experience API payload guard and types definitions end ======
/**
 * Legacy interaction identifier payload guard
 *
 * @remark
 * Following are the types for legacy interaction APIs. They are all treated as deprecated, and can be removed
 * once the new Experience API are fully implemented and migrated.
 * =================================================================================================================
 */
/**
 * Detailed interaction identifier payload guard
 */
const usernamePasswordPayloadGuard = z.object({
    username: z.string().min(1),
    password: z.string().min(1),
});
export const emailPasswordPayloadGuard = z.object({
    email: z.string().min(1),
    password: z.string().min(1),
});
export const phonePasswordPayloadGuard = z.object({
    phone: z.string().min(1),
    password: z.string().min(1),
});
export const socialConnectorPayloadGuard = z.object({
    connectorId: z.string(),
    connectorData: jsonObjectGuard,
});
export const socialEmailPayloadGuard = z.object({
    connectorId: z.string(),
    email: z.string(),
});
export const socialPhonePayloadGuard = z.object({
    connectorId: z.string(),
    phone: z.string(),
});
export const identifierPayloadGuard = z.union([
    usernamePasswordPayloadGuard,
    emailPasswordPayloadGuard,
    phonePasswordPayloadGuard,
    emailVerificationCodePayloadGuard,
    phoneVerificationCodePayloadGuard,
    socialConnectorPayloadGuard,
    socialEmailPayloadGuard,
    socialPhonePayloadGuard,
]);
export const profileGuard = z.object({
    username: z.string().regex(usernameRegEx).optional(),
    email: z.string().regex(emailRegEx).optional(),
    phone: z.string().regex(phoneRegEx).optional(),
    connectorId: z.string().optional(),
    password: z.string().optional(),
});
export var MissingProfile;
(function (MissingProfile) {
    MissingProfile["username"] = "username";
    MissingProfile["email"] = "email";
    MissingProfile["phone"] = "phone";
    MissingProfile["password"] = "password";
    MissingProfile["emailOrPhone"] = "emailOrPhone";
    MissingProfile["extraProfile"] = "extraProfile";
})(MissingProfile || (MissingProfile = {}));
export const bindTotpPayloadGuard = z.object({
    // Unlike identifier payload which has indicator like "email",
    // mfa payload must have an additional type field to indicate type
    type: z.literal(MfaFactor.TOTP),
    code: z.string(),
});
export const bindWebAuthnPayloadGuard = z.object({
    type: z.literal(MfaFactor.WebAuthn),
    id: z.string(),
    rawId: z.string(),
    /**
     * The response from WebAuthn API
     *
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/PublicKeyCredential}
     */
    response: z.object({
        clientDataJSON: z.string(),
        attestationObject: z.string(),
        authenticatorData: z.string().optional(),
        transports: webAuthnTransportGuard.array().optional(),
        publicKeyAlgorithm: z.number().optional(),
        publicKey: z.string().optional(),
    }),
    authenticatorAttachment: z.enum(['cross-platform', 'platform']).optional(),
    clientExtensionResults: z.object({
        appid: z.boolean().optional(),
        crepProps: z
            .object({
            rk: z.boolean().optional(),
        })
            .optional(),
        hmacCreateSecret: z.boolean().optional(),
    }),
});
export const bindBackupCodePayloadGuard = z.object({
    type: z.literal(MfaFactor.BackupCode),
});
export const bindMfaPayloadGuard = z.discriminatedUnion('type', [
    bindTotpPayloadGuard,
    bindWebAuthnPayloadGuard,
    bindBackupCodePayloadGuard,
]);
/** @deprecated  Legacy interaction API use only */
export const totpVerificationPayloadGuard = bindTotpPayloadGuard;
export const webAuthnVerificationPayloadGuard = bindWebAuthnPayloadGuard
    .omit({ response: true })
    .extend({
    response: z.object({
        clientDataJSON: z.string(),
        authenticatorData: z.string(),
        signature: z.string(),
        userHandle: z.string().optional(),
    }),
});
export const backupCodeVerificationPayloadGuard = z.object({
    type: z.literal(MfaFactor.BackupCode),
    code: z.string(),
});
export const verifyMfaPayloadGuard = z.discriminatedUnion('type', [
    totpVerificationPayloadGuard,
    webAuthnVerificationPayloadGuard,
    backupCodeVerificationPayloadGuard,
]);
export const pendingTotpGuard = z.object({
    type: z.literal(MfaFactor.TOTP),
    secret: z.string(),
});
export const pendingWebAuthnGuard = z.object({
    type: z.literal(MfaFactor.WebAuthn),
    challenge: z.string(),
});
export const pendingBackupCodeGuard = z.object({
    type: z.literal(MfaFactor.BackupCode),
    codes: z.array(z.string()),
});
export const pendingEmailVerificationCodeGuard = z.object({
    type: z.literal(MfaFactor.EmailVerificationCode),
    email: z.string(),
});
export const pendingPhoneVerificationCodeGuard = z.object({
    type: z.literal(MfaFactor.PhoneVerificationCode),
    phone: z.string(),
});
// Some information like TOTP secret should be generated in the backend
// and stored in the interaction temporarily.
export const pendingMfaGuard = z.discriminatedUnion('type', [
    pendingTotpGuard,
    pendingWebAuthnGuard,
    pendingBackupCodeGuard,
    pendingEmailVerificationCodeGuard,
    pendingPhoneVerificationCodeGuard,
]);
export const bindTotpGuard = pendingTotpGuard;
export const bindWebAuthnGuard = z.object({
    type: z.literal(MfaFactor.WebAuthn),
    rpId: z.string(),
    credentialId: z.string(),
    publicKey: z.string(),
    transports: webAuthnTransportGuard.array(),
    counter: z.number(),
    agent: z.string(),
    name: z.string().optional(),
});
export const bindBackupCodeGuard = pendingBackupCodeGuard;
// The type for binding new mfa verification to a user, not always equals to the pending type.
export const bindMfaGuard = z.discriminatedUnion('type', [
    bindTotpGuard,
    bindWebAuthnGuard,
    bindBackupCodeGuard,
]);
export const verifyMfaResultGuard = z.object({
    type: z.nativeEnum(MfaFactor),
    id: z.string(),
});
/* eslint-enable max-lines */
